import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { showToast } from '@/services/toast.service';
import TenantConfigService, { CustomDomainCert } from '@/services/tenantConfig.service';

interface CustomDomainCardProps {
  tenantConfigId: string;
  initialDomain: string;
  initialCert: CustomDomainCert | null;
}

export default function CustomDomainCard({
  tenantConfigId,
  initialDomain,
  initialCert,
}: CustomDomainCardProps) {
  const queryClient = useQueryClient();
  const [customDomain, setCustomDomain] = useState(initialDomain);
  const [savingDomain, setSavingDomain] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [certInfo, setCertInfo] = useState<CustomDomainCert | null>(initialCert);
  const [justAttached, setJustAttached] = useState(false);

  useEffect(() => {
    setCustomDomain(initialDomain);
    setCertInfo(initialCert);
    setJustAttached(false);
  }, [initialDomain, initialCert]);

  const saveCustomDomain = useCallback(async () => {
    if (!tenantConfigId) return;

    setSavingDomain(true);
    try {
      const result = await TenantConfigService.getInstance().saveCustomDomain(
        tenantConfigId,
        customDomain || null
      );
      setCertInfo(result.custom_domain_cert || null);
      showToast.success('Custom domain saved');
      queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
      queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
    } catch (err: any) {
      showToast.error('Failed to save custom domain', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setSavingDomain(false);
    }
  }, [tenantConfigId, customDomain, queryClient]);

  const verifyDomain = useCallback(async () => {
    if (!tenantConfigId) return;

    setVerifying(true);
    try {
      const result = await TenantConfigService.getInstance().verifyCustomDomain(
        tenantConfigId
      );
      setCertInfo(result.custom_domain_cert);
      if (result.custom_domain_cert.status === 'ATTACHED') {
        setJustAttached(true);
        showToast.success('Certificate verified and attached!');
      } else if (result.custom_domain_cert.status === 'PENDING_VALIDATION') {
        showToast.info('Certificate is still pending DNS validation.');
      } else if (result.custom_domain_cert.status === 'FAILED') {
        showToast.error('Certificate validation failed.');
      }
      queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
    } catch (err: any) {
      showToast.error('Failed to verify domain', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setVerifying(false);
    }
  }, [tenantConfigId, queryClient]);

  const certStatus = certInfo?.status;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <CardTitle>Custom Domain</CardTitle>
          {certStatus === 'PENDING_VALIDATION' && (
            <Badge variant="outline" className="border-yellow-500 text-yellow-600">
              Pending Validation
            </Badge>
          )}
          {certStatus === 'ATTACHED' && (
            <Badge variant="outline" className="border-green-500 text-green-600">
              Active
            </Badge>
          )}
          {certStatus === 'FAILED' && (
            <Badge variant="destructive">
              Failed
            </Badge>
          )}
        </div>
        <CardDescription>
          Configure a custom domain (CNAME) for your account.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Domain</Label>
          <div className="flex gap-2">
            <Input
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value)}
              placeholder="analytics.yourcompany.com"
            />
            {customDomain && (
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0"
                onClick={() => window.open(`https://${customDomain}`, '_blank')}
                title="Open in new tab"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Enter the custom domain that you wish to point at Pliable.
          </p>
        </div>

        <Button onClick={saveCustomDomain} disabled={savingDomain || customDomain === initialDomain}>
          {savingDomain ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            'Save Domain'
          )}
        </Button>

        {/* Pending validation — show DNS instructions */}
        {certStatus === 'PENDING_VALIDATION' && certInfo && (
          <Alert className="border-yellow-200 bg-yellow-50">
            <AlertDescription className="space-y-3">
              <p className="font-medium">Add the following DNS records to complete setup:</p>

              {certInfo.validation_cname_name && certInfo.validation_cname_value && (
                <div className="space-y-1">
                  <p className="text-sm font-medium">1. Certificate Validation (CNAME)</p>
                  <div className="bg-white rounded border p-2 font-mono text-xs space-y-1">
                    <div><span className="text-muted-foreground">Name: </span>{certInfo.validation_cname_name}</div>
                    <div><span className="text-muted-foreground">Value: </span>{certInfo.validation_cname_value}</div>
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <p className="text-sm font-medium">2. Domain CNAME</p>
                <div className="bg-white rounded border p-2 font-mono text-xs space-y-1">
                  <div><span className="text-muted-foreground">Name: </span>{customDomain}</div>
                  <div><span className="text-muted-foreground">Value: </span>{certInfo.cname_target}</div>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={verifyDomain}
                disabled={verifying}
              >
                {verifying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Verifying...
                  </>
                ) : (
                  'Verify Certificate'
                )}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Attached — show success only right after verify */}
        {certStatus === 'ATTACHED' && certInfo && justAttached && (
          <Alert className="border-green-200 bg-green-50">
            <AlertDescription>
              <p>Custom Domain ready and active.</p>
              {certInfo.attached_at && (
                <p className="text-sm text-muted-foreground mt-1">
                  Attached at: {new Date(certInfo.attached_at).toLocaleString()}
                </p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Failed — show error */}
        {certStatus === 'FAILED' && (
          <Alert variant="destructive">
            <AlertDescription>
              Certificate validation failed. Try saving the domain again to request a new certificate.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
